import useAPI from "@toothfairy/shared-api/useApi";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { Popable } from "react-native-popable";
import envConfig from "../../../../envConfig";
import Files from "../../../API/Files";
import trainingAPI from "../../../API/Training/trainingAPI";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import { generateRowId } from "../../../Utility";
import {
  buildClipboardFile,
  captureTokenFromNameOrUrl,
  clipboardCaptureLabel,
  detectClipboardSource,
  ENABLE_CHIP_COMPOSER,
  extractClipboardParts,
  insertTokenAtCursor,
  normalizeCaptureFileName,
  shouldCaptureAsFile,
  tokenizeForDisplay,
} from "../../../Utility/clipboardUtils";
import { excelHtmlToXlsxFile } from "../../../Utility/clipboardFileConversion";
import { convertImageToPng } from "../../../Utility/imageUtils";
import TokenComposerInput from "../../../Components/TokenComposerInput";
import ChatMediaLeaf from "./ChatMediaLeaf";

function hexToRGB(hex, alpha) {
  let expandedHex = hex;
  if (hex.length === 4) {
    expandedHex = "#" + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }
  const r = parseInt(expandedHex.slice(1, 3), 16);
  const g = parseInt(expandedHex.slice(3, 5), 16);
  const b = parseInt(expandedHex.slice(5, 7), 16);
  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } else {
    return `rgb(${r}, ${g}, ${b})`;
  }
}

const ChatWebInput = ({
  placeholder: placeholderProp,
  onSend = () => {},
  isStreaming,
  onCancel = () => {},
  agentColor,
  allowStop = false,
  isLastMessageEmpty,
  quickQuestions = [],
  isChatEmpty = false,
  allowFileUpload = false,
  allowImagesUpload = false,
  allowAudiosUpload = false,
  allowCodeUpload = false,
  onFileUploaded = () => {},
  onFilesUploaded = () => {},
  onImageUploaded = () => {},
  onAudioUploaded = () => {},
  fileUrl,
  setFileUrl,
  fileUrls,
  setFileUrls,
  imageUrl,
  setImageUrl,
  audioUrl,
  setAudioUrl,
  imageUrls,
  audioUrls,
}) => {
  const { t } = useTranslation();
  const placeholder = placeholderProp || t("arena.webinput.placeholder");
  const AppToasty = AppToast.useToast();
  const { currentTheme, mode } = AppTheme.useTheme();
  const textareaRef = useRef();
  // Counts clipboard-pasted images so each gets a unique name (image 1, image 2, ...)
  const pastedImageCounterRef = useRef(0);
  // Counts clipboard captures of rich/large text (<clipboard 1>, <clipboard 2>, ...)
  const pastedClipboardCounterRef = useRef(0);
  // Friendly labels for clipboard capture pills, e.g. "<clipboard 1>" ->
  // "Excel capture 1" (set at paste time from the detected source app)
  const [clipboardLabels, setClipboardLabels] = useState({});
  // Composer text mirror (used to hide attachment chips whose capture token
  // is inlined in the input; deleting the pill makes the chip reappear)
  const [composerText, setComposerText] = useState("");
  const captureTokensInText = useMemo(
    () =>
      new Set(
        tokenizeForDisplay(composerText)
          .filter((segment) => segment.kind !== "text")
          .map((segment) => segment.value),
      ),
    [composerText],
  );
  const isCaptureChipVisible = (nameOrUrl) => {
    const token = captureTokenFromNameOrUrl(nameOrUrl);
    return !token || !captureTokensInText.has(token);
  };
  // Deleting a capture pill (backspace or inline ✕) removes the attachment
  // itself - reconciliation keeps files and tokens 1:1. Only files we
  // captured from the clipboard are swept (never regular user uploads). The
  // send guard prevents stripping attachments while a message is being sent
  // (send clears the text first).
  const capturedFileNamesRef = useRef(new Set());
  const sendingRef = useRef(false);
  useEffect(() => {
    if (sendingRef.current) return;
    const sweep = (urls, kind) => {
      // Collect first, remove in descending index order: each removal
      // splices the parent's array, so ascending indices would go stale
      const removals = [];
      (urls || []).forEach((url, index) => {
        const base = normalizeCaptureFileName(
          decodeURIComponent((url || "").split("?")[0].split("/").pop() || ""),
        );
        if (!capturedFileNamesRef.current.has(base)) return;
        const token = captureTokenFromNameOrUrl(url);
        if (token && !captureTokensInText.has(token)) {
          capturedFileNamesRef.current.delete(base);
          removals.push(index);
        }
      });
      removals
        .sort((a, b) => b - a)
        .forEach((index) => onFilesUploaded(null, null, index, kind));
    };
    sweep(fileUrls, "file");
    sweep(imageUrls, "image");
  }, [captureTokensInText, fileUrls, imageUrls]);
  const markSending = () => {
    sendingRef.current = true;
    setTimeout(() => {
      sendingRef.current = false;
    }, 0);
  };
  const [text, setText] = React.useState("");
  const [inputReady, setInputReady] = React.useState(false);
  const [isTyping, setIsTyping] = React.useState(false);
  const [prevScrollHeight, setPrevScrollHeight] = React.useState(0);
  const [isScrollingUp, setIsScrollingUp] = React.useState(true);
  const [prevScrollTop, setPrevScrollTop] = React.useState(0);
  const [fileUploadInProgress, setFileUploadInProgress] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [fileUploadStatus, setFileUploadStatus] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const { getActiveWorkspace } = useWorkspaces();
  const source = axios.CancelToken.source();

  const {
    data: S3uploadUrlData,
    loading: urlUploadGenerationInProgress,
    apiRequest: S3uploadUrlRequest,
    response: S3uploadResponse,
  } = useAPI(trainingAPI.bulkImportUrlGeneration, envConfig);
  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);

  const acceptedFileTypes = useMemo(() => {
    const types = [];
    if (allowImagesUpload) {
      types.push(
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/gif",
        "image/bmp",
        "image/webp",
      );
    }
    if (allowAudiosUpload) {
      types.push("audio/wav", "audio/mp4", "audio/m4a", "audio/mp3");
    }
    if (allowFileUpload) {
      types.push(
        ".pptx",
        ".ppt",
        ".json",
        ".pdf",
        ".csv",
        ".doc",
        ".docx",
        ".xls",
        ".xlsx",
        ".html",
        ".txt",
        ".md",
      );
      if (allowCodeUpload) {
        types.push(
          ".py",
          ".js",
          ".java",
          ".cpp",
          ".c",
          ".cs",
          ".php",
          ".rb",
          ".swift",
          ".go",
          ".ts",
          ".rs",
          ".kt",
          ".scala",
          ".m",
          ".r",
          ".pl",
          ".sh",
          ".sql",
          ".vb",
          ".lua",
          ".dart",
          ".groovy",
          ".jl",
          ".hs",
          ".f90",
          ".pas",
          ".elm",
          ".clj",
        );
      }
    }
    return types.join(", ");
  }, [allowImagesUpload, allowAudiosUpload, allowFileUpload, allowCodeUpload]);

  const showUploadOption = useMemo(() => {
    return allowImagesUpload || allowAudiosUpload || allowFileUpload;
  }, [allowImagesUpload, allowAudiosUpload, allowFileUpload]);

  const isValidFileSize = (file, mb = 20) => {
    const maxSizeInBytes = mb * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  };

  const detectExtensionFromFileName = (fileUurl) => {
    if (!fileUurl) return "";
    try {
      const url = new URL(fileUurl);
      const pathname = url.pathname;
      const lastSlashIndex = pathname.lastIndexOf("/");
      if (lastSlashIndex !== -1) {
        const filename = pathname.substring(lastSlashIndex + 1);
        const dotIndex = filename.lastIndexOf(".");
        if (dotIndex !== -1) {
          return filename.substring(dotIndex + 1);
        }
      }
    } catch (e) {
      const lastSlashIndex = fileUurl.lastIndexOf("/");
      const filename =
        lastSlashIndex !== -1
          ? fileUurl.substring(lastSlashIndex + 1)
          : fileUurl;
      const dotIndex = filename.lastIndexOf(".");
      if (dotIndex !== -1) {
        return filename.substring(dotIndex + 1);
      }
    }
    return "";
  };

  const detectFileName = (fileUurl) => {
    if (!fileUurl) return "file";
    try {
      const url = new URL(fileUurl);
      const pathname = url.pathname;
      const lastSlashIndex = pathname.lastIndexOf("/");
      if (lastSlashIndex !== -1) {
        let filename = pathname.substring(lastSlashIndex + 1);
        const dotIndex = filename.lastIndexOf(".");
        if (dotIndex !== -1) {
          filename = filename.substring(0, dotIndex);
        }
        if (/^\d{13}/.test(filename)) {
          filename = filename.substring(13);
        }
        return filename || "file";
      }
    } catch (e) {
      const lastSlashIndex = fileUurl.lastIndexOf("/");
      let filename =
        lastSlashIndex !== -1
          ? fileUurl.substring(lastSlashIndex + 1)
          : fileUurl;
      const dotIndex = filename.lastIndexOf(".");
      if (dotIndex !== -1) {
        filename = filename.substring(0, dotIndex);
      }
      if (/^\d{13}/.test(filename)) {
        filename = filename.substring(13);
      }
      return filename || "file";
    }
    return "file";
  };

  const getUrl = async (path, type = "image") => {
    let initialPath = "";
    if (type == "image") {
      initialPath = "imported-image";
    } else if (type == "audio") {
      initialPath = "imported_audio_files";
    } else if (type == "file") {
      initialPath = "imported_doc_files";
    } else {
      return "";
    }
    const _url = await S3downloadUrlRequest({
      filename: `${initialPath}/${getActiveWorkspace()?.id}/${path}`,
      context: "pdf",
      workspaceid: getActiveWorkspace()?.id,
    });
    if (_url?.url && type == "image") setImageUrl(_url?.url);
    if (_url?.url && type == "audio") setAudioUrl(_url?.url);
    if (_url?.url && type == "file") return _url?.url;
  };

  const handleBatchFilesSelection = async (files, type = "file") => {
    try {
      if (!files) return;

      const filesArray = Array.from(files);
      const uploadedFilesUrls = [];
      const presignedFilesUrls = [];

      if (
        filesArray?.length > 5 ||
        filesArray?.length + (fileUrls?.length || 0) > 5
      ) {
        AppToasty.show(t("toasts.upload.fileLimit"), {
          type: "danger",
        });
        return;
      }

      const maxFileSize = 20 * 1024 * 1024;
      for (let i = 0; i < filesArray.length; i++) {
        if (filesArray[i].size > maxFileSize) {
          AppToasty.show(
            t("toasts.upload.fileSizeExceeded", {
              fileName: filesArray[i].name,
              maxSize: 20,
            }),
            {
              type: "danger",
            },
          );
          return;
        }
      }

      if (!fileUploadInProgress) {
        setFileUploadInProgress(true);
        setFileUploadProgress(0);
      }
      setFileUploadStatus("uploading");

      const totalFiles = filesArray.length;

      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        let filename = file.name;
        filename = filename.replace(/[^a-zA-Z0-9.]/g, "");
        const _fileN = String(Date.now()) + filename;

        if (filename?.length > 100) {
          setFileUploadInProgress(false);
          setFileUploadProgress(0);
          return AppToasty.show(t("toasts.upload.fileNameLength"), {
            type: "danger",
          });
        }
        const contentType = filename.slice(
          (Math.max(0, filename.lastIndexOf(".")) || Infinity) + 1,
        );
        let importType = "imported-image";
        if (type === "audio") {
          importType = "imported_audio_files";
        } else if (type === "file") {
          importType = "imported_doc_files";
        }
        const result = await S3uploadUrlRequest({
          filename: `${getActiveWorkspace()?.id}/${_fileN}`,
          importType,
          contentType,
        });

        await axios.request({
          method: "put",
          url: JSON.parse(result?.body)?.uploadURL,
          data: file,
          headers: { "Content-Type": file.type },
          cancelToken: source.token,
          onUploadProgress: (p) => {
            const currentFileProgress = Math.round((p.loaded * 100) / p.total);
            const completedFilesProgress = (i * 100) / totalFiles;
            const currentFileContribution = currentFileProgress / totalFiles;
            const overallProgress = Math.round(
              completedFilesProgress + currentFileContribution,
            );
            setFileUploadProgress(overallProgress);
          },
        });
        if (JSON.parse(result?.body).uploadURL) {
          const _url = await getUrl(_fileN, type);
          uploadedFilesUrls.push(
            `imported_doc_files/${getActiveWorkspace()?.id}/${_fileN}`,
          );
          presignedFilesUrls.push(_url);
        }
      }
      onFilesUploaded(uploadedFilesUrls, presignedFilesUrls);
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setFileUploadInProgress(false);
      setFileUploadProgress(0);
      setFileUploadStatus("");
    }
  };

  const handleUnifiedFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (fileUploadInProgress) {
      AppToasty.show(t("toasts.upload.wait"), {
        type: "warning",
      });
      event.target.value = "";
      return;
    }

    const imageFiles = [];
    const audioFiles = [];
    const docFiles = [];

    files.forEach((file) => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      if (
        (fileType.startsWith("image/") ||
          fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i)) &&
        allowImagesUpload
      ) {
        imageFiles.push(file);
      } else if (
        (fileType.startsWith("audio/") ||
          fileName.match(/\.(wav|m4a|mp3)$/i)) &&
        allowAudiosUpload
      ) {
        audioFiles.push(file);
      } else if (allowFileUpload) {
        const allowedExtensions = [
          ".pdf",
          ".csv",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".html",
          ".txt",
          ".md",
          ".json",
          ".pptx",
          ".ppt",
        ];
        if (allowCodeUpload) {
          allowedExtensions.push(
            ".py",
            ".js",
            ".java",
            ".cpp",
            ".c",
            ".cs",
            ".php",
            ".rb",
            ".swift",
            ".go",
            ".ts",
            ".rs",
            ".kt",
            ".scala",
            ".m",
            ".r",
            ".pl",
            ".sh",
            ".sql",
            ".vb",
            ".lua",
            ".dart",
            ".groovy",
            ".jl",
            ".hs",
            ".f90",
            ".pas",
            ".elm",
            ".clj",
          );
        }

        const hasAllowedExtension = allowedExtensions.some((ext) =>
          fileName.endsWith(ext),
        );
        if (hasAllowedExtension) {
          docFiles.push(file);
        }
      }
    });

    if (imageFiles.length > 0) {
      const needsConversion = imageFiles.some(
        (file) => file.type.startsWith("image/") && file.type !== "image/png",
      );
      if (needsConversion) {
        setFileUploadInProgress(true);
        setFileUploadStatus("converting");
        setFileUploadProgress(0);
      }

      const convertedImages = await Promise.all(
        imageFiles.map(async (file) => {
          if (file.type.startsWith("image/") && file.type !== "image/png") {
            try {
              return await convertImageToPng(file);
            } catch (error) {
              console.error("Failed to convert image:", error);
              return null;
            }
          }
          return file;
        }),
      );
      const validImages = convertedImages.filter((f) => f !== null);
      if (validImages.length > 0) {
        setFileUploadStatus("uploading");
        handleBatchFilesSelection(validImages, "file");
      } else if (needsConversion) {
        setFileUploadInProgress(false);
        setFileUploadStatus("");
      }
    }

    if (audioFiles.length > 0) {
      handleBatchFilesSelection(audioFiles, "file");
    }

    if (docFiles.length > 0) {
      handleBatchFilesSelection(docFiles, "file");
    }

    event.target.value = "";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isStreaming) return;
    if (fileUploadInProgress) {
      AppToasty.show(t("toasts.upload.waitOperation"), {
        type: "warning",
      });
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const imageFiles = [];
    const audioFiles = [];
    const docFiles = [];

    files.forEach((file) => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      if (
        (fileType.startsWith("image/") ||
          fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i)) &&
        allowImagesUpload
      ) {
        imageFiles.push(file);
      } else if (
        (fileType.startsWith("audio/") ||
          fileName.match(/\.(wav|m4a|mp3)$/i)) &&
        allowAudiosUpload
      ) {
        audioFiles.push(file);
      } else if (allowFileUpload) {
        const allowedExtensions = [
          ".pdf",
          ".csv",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".html",
          ".txt",
          ".md",
          ".json",
          ".pptx",
          ".ppt",
        ];
        if (allowCodeUpload) {
          allowedExtensions.push(
            ".py",
            ".js",
            ".java",
            ".cpp",
            ".c",
            ".cs",
            ".php",
            ".rb",
            ".swift",
            ".go",
            ".ts",
            ".rs",
            ".kt",
            ".scala",
            ".m",
            ".r",
            ".pl",
            ".sh",
            ".sql",
            ".vb",
            ".lua",
            ".dart",
            ".groovy",
            ".jl",
            ".hs",
            ".f90",
            ".pas",
            ".elm",
            ".clj",
          );
        }
        const hasAllowedExtension = allowedExtensions.some((ext) =>
          fileName.endsWith(ext),
        );
        if (hasAllowedExtension) {
          docFiles.push(file);
        }
      }
    });

    if (imageFiles.length > 0) {
      handleBatchFilesSelection(imageFiles, "file");
    }
    if (audioFiles.length > 0) {
      handleBatchFilesSelection(audioFiles, "file");
    }
    if (docFiles.length > 0) {
      handleBatchFilesSelection(docFiles, "file");
    }
  };

  // Handle clipboard paste for images
  // Mac/clipboard pastes name images generically ("image"): number them so
  // consecutive pastes don't collide as the same file (image 1, image 2, ...).
  // The counter is assigned by the paste handler so the inline <image N> token
  // always matches the uploaded file name.
  const renamePastedGenericImage = (file, counter) => {
    const dotIndex = file?.name?.lastIndexOf(".") ?? -1;
    const baseName =
      dotIndex > 0 ? file.name.substring(0, dotIndex) : file.name;
    if (baseName?.toLowerCase() !== "image") return file;
    const extension = dotIndex > 0 ? file.name.substring(dotIndex) : ".png";
    return new File([file], `image ${counter}${extension}`, {
      type: file.type,
    });
  };

  // Inserts a token at the caret: a chip in the chip composer, plain text in
  // the fallback textarea. The fallback path also syncs composerText so the
  // attachment sweep sees the token (the composer updates it via onChange).
  const insertToken = (token) => {
    if (textareaRef.current?.insertToken) {
      textareaRef.current.insertToken(token);
    } else {
      insertTokenAtCursor(textareaRef.current, token, (value) => {
        setText(value);
        setComposerText(value);
      });
    }
  };

  // Clipboard capture: rich/large text becomes a file attachment referenced
  // inline with a <clipboard N> token; pure image pastes upload the image and
  // reference it with an <image N> token. Shared by the document-level
  // listener (fallback textarea mode) and the composer's onPaste.
  const handleClipboardPasteCapture = (event) => {
    if (!allowImagesUpload && !allowFileUpload) return;

    const { htmlString, plainString, imageItem } = extractClipboardParts(
      event.clipboardData,
    );
    if (!htmlString && !plainString && !imageItem) return;

    // Pure image paste (screenshot, copied image - no text flavours):
    // upload as a file and reference it inline with an <image N> token.
    if (imageItem && !htmlString && !plainString) {
      if (!allowImagesUpload) return;
      event.preventDefault();

      const file = imageItem.getAsFile();
      if (!file) return;

      if (!isValidFileSize(file)) {
        AppToasty.show(t("toasts.upload.imageSize"), {
          type: "danger",
        });
        return;
      }

      const imageCounter = ++pastedImageCounterRef.current;

      // Normalise non-PNG clipboard formats (Mac screenshots can arrive as
      // TIFF) to PNG first so downstream classification by extension works.
      (async () => {
        let fileToUpload = file;
        if (file.type?.startsWith("image/") && file.type !== "image/png") {
          try {
            fileToUpload = await convertImageToPng(file);
          } catch (error) {
            console.error("Failed to convert pasted image:", error);
          }
        }
        const renamed = renamePastedGenericImage(fileToUpload, imageCounter);
        capturedFileNamesRef.current.add(normalizeCaptureFileName(renamed.name));
        handleBatchFilesSelection([renamed], "file");
      })();
      insertToken(`<image ${imageCounter}>`);
      return;
    }

    // Rich/large text paste (Office suite, web pages, big plain text):
    // capture the clipboard content as a file attachment and reference it
    // inline with a <clipboard N> token instead of dumping a raw text blob
    // into the input.
    if (!allowFileUpload) return;
    if (!shouldCaptureAsFile({ htmlString, plainString })) return;

    event.preventDefault();
    const clipboardCounter = ++pastedClipboardCounterRef.current;
    const source = detectClipboardSource(htmlString);
    let file = buildClipboardFile({
      htmlString,
      plainString,
      counter: clipboardCounter,
    });

    // Excel pastes become real .xlsx files (SheetJS converts the Office HTML
    // table); other sources keep the HTML capture which preserves formatting.
    if (source === "excel" && htmlString) {
      try {
        file = excelHtmlToXlsxFile(htmlString, `clipboard ${clipboardCounter}`);
      } catch (error) {
        console.error("Failed to convert Excel paste to xlsx:", error);
      }
    }

    // Same size validation as regular file uploads (20MB)
    if (file.size > 20 * 1024 * 1024) {
      AppToasty.show(
        t("toasts.upload.fileSizeExceeded", {
          fileName: file.name,
          maxSize: 20,
        }),
        { type: "danger" },
      );
      return;
    }

    handleBatchFilesSelection([file], "file");
    capturedFileNamesRef.current.add(normalizeCaptureFileName(file.name));
    setClipboardLabels((previous) => ({
      ...previous,
      [`<clipboard ${clipboardCounter}>`]: clipboardCaptureLabel(
        source,
        clipboardCounter,
      ),
    }));
    insertToken(`<clipboard ${clipboardCounter}>`);
  };

  // Keep the latest handler in a ref so the document-level listener stays
  // stable while always seeing current props/state.
  const pasteHandlerRef = useRef();
  pasteHandlerRef.current = handleClipboardPasteCapture;

  useEffect(() => {
    const listener = (event) => {
      // Pastes aimed at another editable element (search box, modal field,
      // another Arena pane) belong to that element - leave them alone. The
      // composer handles its own pastes via onPaste + stopPropagation (this
      // listener never sees them), so this document-level path serves only
      // the fallback textarea and "paste anywhere with nothing focused".
      const editableTarget = event.target?.closest?.(
        'input, textarea, [contenteditable]:not([contenteditable="false"])',
      );
      if (editableTarget && event.target !== textareaRef.current) return;
      pasteHandlerRef.current?.(event);
    };
    document.addEventListener("paste", listener);
    return () => {
      document.removeEventListener("paste", listener);
    };
  }, []);

  // Composer pastes are handled element-scoped; stop propagation so the
  // document listener does not double-handle them.
  const handleComposerPaste = (event) => {
    pasteHandlerRef.current?.(event);
    event.stopPropagation();
  };

  useEffect(() => {
    const handleDragEnd = () => {
      setIsDragging(false);
    };
    document.addEventListener("dragend", handleDragEnd);
    return () => {
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, []);

  useEffect(() => {
    let currentText = "";
    const words = placeholder?.split(" ");
    if (words?.length > 0)
      setTimeout(() => {
        const intervalId = setInterval(() => {
          if (words.length > 0) {
            currentText += words.shift() + " ";
            setText(currentText);
          } else {
            clearInterval(intervalId);
            setInputReady(true);
            setTimeout(() => {
              textareaRef.current?.focus();
            }, 100);
          }
        }, 30);
      }, 20);
  }, [placeholder]);

  React.useEffect(() => {
    if (!isStreaming) textareaRef.current?.focus();
  }, [isStreaming]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      const handleScroll = () => {
        const currentScrollHeight = textarea.scrollHeight;
        const scrollTop = textarea.scrollTop;
        if (textarea.scrollTop < prevScrollTop) {
          setIsScrollingUp(true);
        } else {
          setIsScrollingUp(false);
        }
        setPrevScrollTop(textarea.scrollTop);
        if (prevScrollHeight > currentScrollHeight || isScrollingUp) {
          textarea.removeEventListener("scroll", handleScroll);
          setPrevScrollHeight(currentScrollHeight);
          return;
        }
      };

      textarea.addEventListener("scroll", handleScroll);
      textarea.addEventListener("input", () => setIsTyping(true));
      textarea.addEventListener("compositionstart", () => setIsTyping(true));
      textarea.addEventListener("compositionend", () => setIsTyping(false));

      setPrevScrollHeight(textarea.scrollHeight);

      return () => {
        textarea.removeEventListener("scroll", handleScroll);
        textarea.removeEventListener("input", () => setIsTyping(true));
        textarea.removeEventListener("compositionstart", () =>
          setIsTyping(true),
        );
        textarea.removeEventListener("compositionend", () =>
          setIsTyping(false),
        );
      };
    }
  }, [isTyping]);

  const getPlaceholderText = () => {
    if (fileUploadInProgress) {
      if (fileUploadStatus === "converting") {
        return t("chats.placeholders.preparingFiles");
      }
      return t("chats.placeholders.uploadingFiles");
    }
    return text;
  };

  const handleKeyDown = (event) => {
    const textarea = textareaRef.current;

    if (event.key === "Enter") {
      if (textarea && event.shiftKey) {
        requestAnimationFrame(() => {
          textarea.scrollTop = textarea.scrollTop += 25;
        });
      }
      if (!event.shiftKey) {
        event.preventDefault();
        if (textareaRef.current.value?.length > 10000) {
          return AppToasty.show(t("arena.webinput.maxChars"), {
            type: "danger",
          });
        }
        if (!isStreaming && !fileUploadInProgress) {
          onSend(textareaRef.current.value);
          markSending();
          if (textareaRef.current) textareaRef.current.value = "";
        }
      }
    }
  };

  const handleSend = () => {
    if (textareaRef.current.value?.length > 10000) {
      return AppToasty.show(t("arena.webinput.maxChars"), {
        type: "danger",
      });
    }
    if (!isStreaming && !fileUploadInProgress) {
      onSend(textareaRef.current.value);
      markSending();
      textareaRef.current.value = null;
      textareaRef.current?.focus();
    }
  };

  return (
    <div
      style={{
        position: "relative",
        marginBottom: 5,
        marginRight: 5,
        marginLeft: 5,
        flexShrink: 0,
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <View
        style={{
          width: "100%",
          flexDirection: "row",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        {showUploadOption && (
          <View
            style={{
              flexDirection: "column",
              alignItems: "flex-start",
              maxHeight: 120,
              overflowY: "auto",
              maxWidth: "100%",
              flexShrink: 1,
              gap: 6,
              marginLeft: 5,
            }}
          >
            {fileUrls &&
              fileUrls
                .map((fUrl, index) => ({ fUrl, index }))
                .filter(({ fUrl }) => isCaptureChipVisible(fUrl))
                .map(({ fUrl, index }) => {
                return (
                  <ChatMediaLeaf
                    key={`file-${index}`}
                    onUploaded={() =>
                      onFilesUploaded(null, null, index, "file")
                    }
                    url={fUrl}
                    type="file"
                    fileName={detectFileName(fUrl)}
                    fileExtension={detectExtensionFromFileName(fUrl)}
                  />
                );
              })}
            {imageUrls &&
              imageUrls
                .map((iUrl, index) => ({ iUrl, index }))
                .filter(({ iUrl }) => isCaptureChipVisible(iUrl))
                .map(({ iUrl, index }) => {
                return (
                  <ChatMediaLeaf
                    key={`image-${index}`}
                    onUploaded={() =>
                      onFilesUploaded(null, null, index, "image")
                    }
                    url={iUrl}
                    type="image"
                    fileName={detectFileName(iUrl)}
                    fileExtension={detectExtensionFromFileName(iUrl)}
                  />
                );
              })}
            {audioUrls &&
              audioUrls.map((aUrl, index) => {
                return (
                  <ChatMediaLeaf
                    key={`audio-${index}`}
                    onUploaded={() =>
                      onFilesUploaded(null, null, index, "audio")
                    }
                    url={aUrl}
                    type="audio"
                    fileName={detectFileName(aUrl)}
                    fileExtension={detectExtensionFromFileName(aUrl)}
                  />
                );
              })}
            {fileUploadInProgress && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  minWidth: 160,
                  marginBottom: 7,
                }}
              >
                <AppText
                  color={currentTheme?.theme?.orange}
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                  }}
                  wrapperStyle={{
                    marginRight: 5,
                  }}
                  preventTranslation
                >
                  {fileUploadStatus === "converting"
                    ? t("chats.placeholders.preparingFiles")
                    : t("chats.placeholders.uploadingFilesWithProgress", {
                        progress: fileUploadProgress,
                      })}
                </AppText>
              </View>
            )}
          </View>
        )}
      </View>
      <div
        className="tf-chat-input"
        style={{
          width: "auto",
          display: "flex",
          flexDirection: "row",
          paddingTop: 10,
          borderRadius: 10,
          border: mode === "blue" && "1px solid #456491",
          paddingLeft: 10,
          paddingRight: 20,
          backgroundColor: isDragging
            ? currentTheme?.theme?.primary_soft
            : currentTheme?.theme?.lightGrey,
          justifyContent: "space-between",
          minHeight: 80,
          borderWidth: isDragging ? 2 : 0,
          borderColor: isDragging
            ? currentTheme?.theme?.primary
            : currentTheme?.theme?.transparent,
          borderStyle: isDragging ? "dashed" : "solid",
        }}
      >
        {isDragging && (
          <View
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: currentTheme?.theme?.primary_soft,
              borderRadius: 5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 999,
              pointerEvents: "none",
            }}
          >
            <AppText
              style={{
                fontSize: currentTheme?.theme?.medium_font_size,
                fontWeight: "bold",
              }}
              color={currentTheme?.theme?.primary}
            >
              Drop files here
            </AppText>
            <AppText
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                marginTop: 5,
              }}
              color={currentTheme?.theme?.darkGrey}
            >
              Images, audios, and documents
            </AppText>
          </View>
        )}
        <View
          style={{ position: "relative", flex: 1, flexDirection: "column" }}
        >
          {ENABLE_CHIP_COMPOSER ? (
            <TokenComposerInput
              ref={textareaRef}
              placeholder={getPlaceholderText()}
              currentTheme={currentTheme}
              opacity={isDragging ? 0.3 : 1}
              disabled={!inputReady}
              onKeyDown={handleKeyDown}
              onPaste={handleComposerPaste}
              onChange={({ text: nextText }) => setComposerText(nextText)}
              labelMap={clipboardLabels}
              // The Arena container is content-height (minHeight 80, no max):
              // cap the composer so it scrolls internally like the textarea did
              style={{ maxHeight: 70 }}
            />
          ) : (
            <textarea
              disabled={!inputReady}
              ref={textareaRef}
              placeholder={getPlaceholderText()}
              className="tf-chat-input-textarea"
              style={{
                fontFamily: "normalFont",
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.font_color,
                backgroundColor: currentTheme?.theme?.transparent,
                flex: 1,
                resize: "none",
                opacity: isDragging ? 0.3 : 1,
              }}
              onKeyDown={handleKeyDown}
              onInput={(event) => setComposerText(event.target.value)}
            />
          )}
        </View>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            height: "fit-content",
          }}
        >
          {showUploadOption && !isStreaming && (
            <Popable
              content={t("ui.uploadFiles")}
              action="hover"
              position="bottom"
              backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
              style={{ top: 20, zIndex: 20, maxWidth: 300 }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AppIcon
                  isClickable
                  icon={AppAssets.icons.attachment}
                  size={16}
                  onPress={() => fileInputRef.current.click()}
                  color={currentTheme?.theme?.darkGrey}
                  wrapperStyle={{ margin: 0 }}
                />
              </div>
            </Popable>
          )}
          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept={acceptedFileTypes}
            style={{ display: "none" }}
            onChange={handleUnifiedFileUpload}
          />
          <div className="tf-chat-input-icon" style={{ width: 30 }}>
            <AppIcon
              icon={
                isStreaming
                  ? AppAssets?.icons?.stopGeneration
                  : AppAssets?.icons?.send
              }
              color={
                mode === "blue"
                  ? currentTheme?.theme?.white
                  : isStreaming
                  ? currentTheme?.theme?.red
                  : agentColor || currentTheme?.theme?.primary
              }
              size={20}
              wrapperStyle={{
                marginRight: 0,
                marginLeft: 8,
                marginTop: 0,
                justifyContent: "center",
                alignSelf: "baseline",
              }}
              isClickable={!isLastMessageEmpty}
              onPress={
                !isLastMessageEmpty && isStreaming ? onCancel : handleSend
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatWebInput;
